import { Dayjs } from "@/common/utils/dayjsUtil";
import { CustomProvider, DateRangePicker } from "rsuite";
import { IDateTimeRangeModel } from "../../../models/model";
import "./datetimeRangePicker.scss";
import { useTranslation } from "react-i18next";
import deDE from "rsuite/locales/de_DE";
import esES from "rsuite/locales/es_ES";
import frFR from "rsuite/locales/fr_FR";
import itIT from "rsuite/locales/it_IT";
import ptBR from "rsuite/locales/pt_BR";
import enEN from "rsuite/locales/en_US";
import csCS from "@/locales/cs_CS";
import { IUserProfileModel } from "../../../../store/auth/types";

export default function DatetimeRangePicker(props: IDateTimeRangeModel) {
  const removeOverflow = () => {
    const mainBody = document.getElementById("main_body_id");
    if (mainBody) {
      mainBody.style.overflow = "hidden";
    }
  };

  const addOverflow = () => {
    const mainBody = document.getElementById("main_body_id");
    if (mainBody) {
      mainBody.style.overflow = "auto";
    }
  };

  const { t: translate } = useTranslation();
  const userInfo = localStorage.getItem("user_info") || "";
  const parsed: IUserProfileModel = JSON.parse(userInfo);
  const userLocale: string = parsed.locale || "en_US";
  const myLocal = {
    cs_CZ: csCS,
    fr_FR: frFR,
    es_ES: esES,
    de_DE: deDE,
    pt_PT: ptBR,
    it_IT: itIT,
    en_US: enEN,
  };

  // Helper function to convert Dayjs to Date consistently
  const toSafeDate = (dayjsObj: any) => {
    // Use the local() method to ensure we're working in local timezone
    // and valueOf() to get the timestamp, then create a new Date
    return new Date(dayjsObj.local().valueOf());
  };

  // Calculate the date 2 years ago from now using consistent timezone handling
  const twoYearsAgo = toSafeDate(Dayjs().subtract(2, "year").startOf("day"));
  const today = toSafeDate(Dayjs().endOf("day"));
  // restrict last 6 months
  const sixMonthsAgo = toSafeDate(Dayjs().subtract(6, "month").startOf("day"));
  // Function to validate the date range
  const shouldDisableDate = (date: Date) => {
    if (props.restrictToLast6Months) {
      return (
        date.getTime() < sixMonthsAgo.getTime() ||
        date.getTime() > today.getTime()
      );
    }
    if (props.restrictToLastTwoYears) {
      // Compare dates using getTime() to avoid timezone issues
      return (
        date.getTime() < twoYearsAgo.getTime() ||
        date.getTime() > today.getTime()
      );
    }
    return false;
  };

  const getValidRange = (start: Date, end: Date): [Date, Date] => {
    let minDate = twoYearsAgo;

    if (props.restrictToLast6Months) {
      minDate = sixMonthsAgo;
    }

    const maxDate = toSafeDate(Dayjs().endOf("day"));

    const safeStart = start.getTime() < minDate.getTime() ? minDate : start;
    const safeEnd = end.getTime() > maxDate.getTime() ? maxDate : end;

    return [safeStart, safeEnd];
  };

  // Enhanced date range shortcuts with consistent timezone handling
  const getDateRangeShortcuts = () => [
    {
      label: `${translate("logs.search_one_minute")}`,
      value: (): [Date, Date] => [
        toSafeDate(Dayjs().subtract(1, "minute")),
        toSafeDate(Dayjs()),
      ],
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_10_minutes")}`,
      value: (): [Date, Date] => [
        toSafeDate(Dayjs().subtract(10, "minute")),
        toSafeDate(Dayjs()),
      ],
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_30_minutes")}`,
      value: (): [Date, Date] => [
        toSafeDate(Dayjs().subtract(30, "minute")),
        toSafeDate(Dayjs()),
      ],
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_one_hour")}`,
      value: (): [Date, Date] => [
        toSafeDate(Dayjs().subtract(1, "hour")),
        toSafeDate(Dayjs()),
      ],
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_24_hours")}`,
      value: (): [Date, Date] => [
        toSafeDate(Dayjs().subtract(24, "hour")),
        toSafeDate(Dayjs()),
      ],
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_today")}`,
      value: (): [Date, Date] => [
        toSafeDate(Dayjs().startOf("day")),
        toSafeDate(Dayjs().endOf("day")),
      ],
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_yesterday")}`,
      value: (): [Date, Date] => {
        const yesterday = Dayjs().subtract(1, "day");
        return [
          toSafeDate(yesterday.startOf("day")),
          toSafeDate(yesterday.endOf("day")),
        ];
      },
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_this_week")}`,
      value: (): [Date, Date] => {
        const weekStart = toSafeDate(Dayjs().startOf("week"));
        const weekEnd = toSafeDate(Dayjs().endOf("week"));
        return getValidRange(weekStart, weekEnd);
      },
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_last_week")}`,
      value: (): [Date, Date] => {
        const lastWeek = Dayjs().subtract(1, "week");
        return [
          toSafeDate(lastWeek.startOf("week")),
          toSafeDate(lastWeek.endOf("week")),
        ];
      },
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_this_month")}`,
      value: (): [Date, Date] => {
        const monthStart = toSafeDate(Dayjs().startOf("month"));
        const monthEnd = toSafeDate(Dayjs().endOf("month"));
        return getValidRange(monthStart, monthEnd);
      },
      placement: "left" as const,
    },
    {
      label: `${translate("logs.search_last_month")}`,
      value: (): [Date, Date] => {
        const lastMonth = Dayjs().subtract(1, "month");
        return [
          toSafeDate(lastMonth.startOf("month")),
          toSafeDate(lastMonth.endOf("month")),
        ];
      },
      placement: "left" as const,
    },
  ];

  // Consistent date value handling
  const getDateValue = (dateString: string) => {
    try {
      return toSafeDate(Dayjs(dateString));
    } catch (error) {
      console.warn("Invalid date string:", dateString);
      return toSafeDate(Dayjs());
    }
  };

  return (
    <CustomProvider locale={myLocal[userLocale]}>
      <DateRangePicker
        className="datetimeRangePicker"
        onOpen={removeOverflow}
        onClose={addOverflow}
        format="MMM dd, yyyy hh:mm aa"
        renderValue={(value) => {
          if (!value || !Array.isArray(value) || value.length !== 2) {
            return "";
          }
          try {
            const startFormatted = Dayjs(value[0]).format(
              "MMM DD, YYYY hh:mm A",
            );
            const endFormatted = Dayjs(value[1]).format("MMM DD, YYYY hh:mm A");
            return `${startFormatted} - ${endFormatted}`;
          } catch (error) {
            console.warn("Error formatting date range:", error);
            return "";
          }
        }}
        placeholder="MMM dd, yyyy hh:mm aa - MMM dd, yyyy hh:mm aa"
        showMeridian
        cleanable={props.cleanable}
        editable={true}
        ranges={props.isDefaultRangeShortcut ? getDateRangeShortcuts() : []}
        defaultValue={[
          getDateValue(props.defaultStartDate),
          getDateValue(props.defaultEndDate),
        ]}
        value={[getDateValue(props.startDate), getDateValue(props.endDate)]}
        onChange={props.onDateRangeChange}
        shouldDisableDate={
          props.restrictToLastTwoYears || props.restrictToLast6Months
            ? shouldDisableDate
            : undefined
        }
      />
    </CustomProvider>
  );
}
